import { useEffect, useRef, useState } from "react";
import { BoardEditor } from "./Atelier";
import { isBoardName, itemTitle } from "./atelier";
import { currentUserId } from "./auth";
import { coffreFileUrl, copyVaultFile, deletePageAndMedia, officeConfig, readCoffreText, saveCoffreText, saveFicheNotes, withForeignNotes, withoutForeignNotes, type CoffreEntry, type CoffreOpenKind } from "./coffre";
import { EML_MAX_BYTES, parseEml, type EmlMessage } from "./eml";
import { Modal } from "./Modal";
import { NotePad, readPad } from "./NotePad";
import { isNotesFile, notesToPad, padToNotes } from "./notesPad";
import { printHtml, printPlain } from "./printPage";
import type { CrmFiche } from "./types";
import { loadCrmWeek } from "./week";

export function CoffreView({ entry, kind, ownerId = 0, fiche, onClose }: { entry: CoffreEntry; kind: CoffreOpenKind; ownerId?: number; fiche: CrmFiche; onClose: () => void }) {
  const board = isBoardName(entry.name);
  const [full, setFull] = useState(kind === "office" || board);
  const [pageText, setPageText] = useState("");
  const [pageHtml, setPageHtml] = useState("");
  const [printError, setPrintError] = useState("");
  const [boardError, setBoardError] = useState("");
  const [fileAsk, setFileAsk] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const guest = ownerId > 0;
  const page = kind === "text" && isMarkdownPage(entry.name);
  const mail = kind === "eml";
  const wide = `crm-pop crm-view-size flex flex-col rounded-2xl border border-slate-200 shadow-2xl ${board ? "overflow-hidden p-0" : `p-4 ${page ? "overflow-visible" : "overflow-hidden"}`} ${full ? "is-full" : board ? "is-board" : "is-pane"}`;

  return (
    <Modal onClose={onClose} hideClose={board} sheetClass={wide}>
      {(close) => (
        <>
          {board ? null : (
            <div className="flex shrink-0 items-center gap-2">
              <h3 className="min-w-0 flex-1 truncate text-base font-semibold">{page || board ? itemTitle(entry.name) : entry.name}</h3>
              {page && !isNotesFile(entry.name) ? (
                <>
                  <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold" disabled={fileBusy} onClick={() => { setCopied(false); setFileBusy(true); void copyVaultFile(entry.path, entry.name, ownerId).then(() => setCopied(true)).catch((reason: unknown) => setPrintError(reason instanceof Error ? reason.message : "Duplication impossible.")).finally(() => setFileBusy(false)); }}>Dupliquer</button>
                  {guest ? null : <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold" disabled={fileBusy} onClick={() => setFileAsk(true)}>Supprimer</button>}
                  <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold" onClick={() => setPrintError(printMarkdown(entry.name, pageText, pageHtml))}>
                    Imprimer
                  </button>
                </>
              ) : null}
              <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold" onClick={() => setFull((value) => !value)}>
                {full ? "Réduire" : "Plein écran"}
              </button>
            </div>
          )}
          {!board && copied ? <p className="mt-2 text-xs text-slate-500">Copie créée à côté de cette page.</p> : null}
          {!board && fileAsk ? (
            <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-950">
              <p>{kind === "image" ? `Supprimer « ${entry.name} » ? La photo part dans la corbeille du Coffre.` : `Supprimer « ${itemTitle(entry.name)} » ? La page part dans la corbeille, ainsi que les photos et fichiers qu’elle est seule à utiliser.`}</p>
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold" onClick={() => setFileAsk(false)}>Annuler</button>
                <button type="button" className="rounded-full bg-rose-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50" disabled={fileBusy} onClick={() => { setFileBusy(true); void deletePageAndMedia(entry.path, ownerId).then(() => close()).catch((reason: unknown) => { setPrintError(reason instanceof Error ? reason.message : "Suppression impossible."); setFileBusy(false); }); }}>Supprimer</button>
              </div>
            </div>
          ) : null}
          {!board && printError ? <p className="mt-2 text-sm text-rose-700">{printError}</p> : null}
          <div className={board ? "flex min-h-0 flex-1 flex-col" : page || full || mail ? "mt-3 min-h-0 flex-1" : "mt-3 h-[68vh]"}>
            {kind === "pdf" ? <iframe title={entry.name} src={`${coffreFileUrl(entry.path, ownerId)}&inline=1`} className="h-full w-full rounded-xl border border-slate-200" /> : null}
            {kind === "image" ? <img src={`${coffreFileUrl(entry.path, ownerId)}&inline=1`} alt={entry.name} className="mx-auto h-full max-w-full object-contain" onContextMenu={guest ? undefined : (event) => { event.preventDefault(); setFileAsk(true); }} /> : null}
            {page ? <PagePane path={entry.path} ownerId={ownerId} onText={setPageText} onHtml={setPageHtml} /> : null}
            {board ? <BoardEditor fiche={fiche} entry={entry} onBack={close} onError={setBoardError} notice={boardError} frame={{ full, onFull: () => setFull((value) => !value) }} /> : null}
            {kind === "text" && !page && !board ? <TextPane path={entry.path} ownerId={ownerId} editing={full} /> : null}
            {kind === "office" ? <OfficePane path={entry.path} ownerId={ownerId} mode={full ? "edit" : "view"} /> : null}
            {mail ? <EmlPane path={entry.path} ownerId={ownerId} size={entry.size} /> : null}
          </div>
        </>
      )}
    </Modal>
  );
}

function printMarkdown(name: string, source: string, html: string): string {
  const title = name.replace(/\.md$/i, "");
  try {
    if (html.trim()) printHtml(title, html);
    else {
      const part = readPad(source);
      const body = part.markdown.trim() || (part.blocks || []).map(blockText).filter(Boolean).join("\n\n");
      printPlain(title, body);
    }
    return "";
  } catch (reason: unknown) {
    return reason instanceof Error ? reason.message : "Impression impossible.";
  }
}

function blockText(block: unknown): string {
  if (!block || typeof block !== "object") return "";
  const content = (block as { content?: unknown }).content;
  if (!Array.isArray(content)) return "";
  return content.map((item) => (item && typeof item === "object" && "text" in item ? String((item as { text?: unknown }).text || "") : "")).join("");
}

function folderOf(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut < 0 ? "" : path.slice(0, cut);
}

function isMarkdownPage(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith(".md") && !lower.endsWith(".board.md");
}

function PagePane({ path, ownerId, onText, onHtml }: { path: string; ownerId: number; onText: (value: string) => void; onHtml: (value: string) => void }) {
  const [initial, setInitial] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [notesRaw, setNotesRaw] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const fullNotes = useRef("");
  const viewerId = useRef(0);
  const sharedTasks = useRef<Set<string> | null>(null);
  const notes = isNotesFile(path.split("/").pop() || "");

  useEffect(() => {
    let alive = true;
    void readCoffreText(path, ownerId)
      .then(async (raw) => {
        if (!alive) return;
        let source = raw;
        fullNotes.current = raw;
        viewerId.current = Number(currentUserId()) || 0;
        sharedTasks.current = null;
        if (notes && ownerId > 0) {
          const board = await loadCrmWeek().catch(() => null);
          if (!alive) return;
          sharedTasks.current = new Set([...(board?.tasks || []), ...(board?.kanbanShared || [])].map((task) => task.id));
          source = withoutForeignNotes(raw, viewerId.current, sharedTasks.current);
        }
        const stitched = notes ? notesToPad(source) : null;
        const guestNotes = notes && ownerId > 0;
        const shown = guestNotes ? (stitched || "") : (stitched || raw);
        setNotesRaw(guestNotes || stitched ? source : null);
        setInitial(shown);
        setText(shown);
        onText(shown);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Page illisible.");
      });
    return () => {
      alive = false;
    };
  }, [path, ownerId, notes, onText]);

  function notesToSave(pad: string) {
    const folder = path.replace(/\/?notes-CRM\.md$/i, "");
    const next = padToNotes(notesRaw || "", pad);
    return {
      folder,
      notes: ownerId > 0 ? withForeignNotes(next, fullNotes.current, viewerId.current, sharedTasks.current) : next,
    };
  }

  async function save() {
    setError("");
    setSaved("");
    try {
      if (notesRaw !== null) {
        const packed = notesToSave(text);
        await saveFicheNotes(packed.folder, packed.notes, true, ownerId);
      } else {
        await saveCoffreText(path, text, ownerId);
      }
      setSaved("Enregistré.");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
    }
  }

  if (error && initial === null) return <p className="text-sm text-rose-700">{error}</p>;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="crm-notepad crm-notepad-page min-h-0 flex-1 rounded-xl border border-slate-200 p-3">
        {initial !== null ? <NotePad key={path} initial={initial} portaled upload={{ folder: folderOf(path), ownerId, pagePath: path }} persist={async (value) => { if (notesRaw !== null) { const packed = notesToSave(value); await saveFicheNotes(packed.folder, packed.notes, true, ownerId); } else await saveCoffreText(path, value, ownerId); }} onChange={(value) => { setText(value); onText(value); }} onHtml={onHtml} /> : <p className="text-sm text-slate-500">Chargement…</p>}
      </div>
      <div className="mt-2 flex items-center justify-end gap-3">
        <button type="button" className="crm-fill rounded-full px-4 py-1.5 text-sm font-semibold text-white" disabled={initial === null} onClick={() => void save()}>Enregistrer</button>
        {saved ? <p className="text-sm text-slate-500">{saved}</p> : null}
        {error ? <p className="text-sm text-rose-700">{error}</p> : null}
      </div>
    </div>
  );
}

function TextPane({ path, ownerId, editing }: { path: string; ownerId: number; editing: boolean }) {
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let alive = true;
    void readCoffreText(path, ownerId)
      .then((text) => {
        if (alive) setBody(text);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Fichier illisible.");
      });
    return () => {
      alive = false;
    };
  }, [path, ownerId]);

  async function save() {
    setError("");
    setSaved("");
    try {
      await saveCoffreText(path, body, ownerId);
      setSaved("Enregistré.");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
    }
  }

  if (error && body === "") return <p className="text-sm text-rose-700">{error}</p>;
  if (!editing) return <pre className="crm-scroll h-full min-h-[50vh] overflow-auto whitespace-pre-wrap rounded-xl bg-black/5 p-4 text-sm">{body}</pre>;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <textarea value={body} onChange={(event) => setBody(event.target.value)} className="crm-field min-h-0 w-full flex-1 resize-none rounded-xl border px-3 py-2 text-sm" />
      <div className="mt-2 flex items-center justify-end gap-3">
        <button type="button" className="crm-fill rounded-full px-4 py-1.5 text-sm font-semibold text-white" onClick={() => void save()}>
          Enregistrer
        </button>
        {saved ? <p className="text-sm text-slate-500">{saved}</p> : null}
        {error ? <p className="text-sm text-rose-700">{error}</p> : null}
      </div>
    </div>
  );
}

function EmlPane({ path, ownerId, size }: { path: string; ownerId: number; size?: number | null }) {
  const [message, setMessage] = useState<EmlMessage | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setMessage(null);
    if (size != null && size > EML_MAX_BYTES) {
      setError("Message trop volumineux pour être lu ici (max. 20 Mo).");
      return () => {
        alive = false;
      };
    }
    setError("");
    void fetch(coffreFileUrl(path, ownerId), { credentials: "include", cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Message illisible.");
        const raw = await res.text();
        if (new TextEncoder().encode(raw).length > EML_MAX_BYTES) throw new Error("Message trop volumineux pour être lu ici (max. 20 Mo).");
        return parseEml(raw);
      })
      .then((parsed) => {
        if (alive) setMessage(parsed);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Message illisible.");
      });
    return () => {
      alive = false;
    };
  }, [path, ownerId, size]);

  if (error) return <p className="text-sm text-rose-700">{error}</p>;
  if (!message) return <p className="text-sm text-slate-500">Chargement…</p>;
  return <EmlMessageView message={message} />;
}

function EmlMessageView({ message }: { message: EmlMessage }) {
  const srcDoc = message.html ? mailDocument(message.html) : "";
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200">
      <div className="shrink-0 space-y-0.5 border-b border-slate-200 px-4 py-3">
        <p className="truncate text-sm font-semibold">{message.subject}</p>
        <EmlMeta label="De" value={message.from} />
        <EmlMeta label="À" value={message.to} />
        <EmlMeta label="Cc" value={message.cc} />
        <EmlMeta label="Le" value={message.dateLabel} />
        {message.attachments.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5 pt-1.5">
            {message.attachments.map((file, index) => (
              <li key={`${file.name}-${index}`} className="max-w-full truncate rounded-full bg-black/5 px-2 py-0.5 text-[11px]" title={file.name}>
                {file.name}
                {file.size ? ` · ${formatMailSize(file.size)}` : ""}
              </li>
            ))}
          </ul>
        ) : null}
        {message.remoteImagesHidden ? <p className="pt-1 text-[11px] opacity-60">Images distantes masquées.</p> : null}
      </div>
      <div className="min-h-0 flex-1">
        {srcDoc ? (
          <iframe title={message.subject || "Message"} sandbox="" srcDoc={srcDoc} className="h-full w-full border-0 bg-white" />
        ) : (
          <pre className="crm-scroll h-full overflow-auto whitespace-pre-wrap px-4 py-3 text-sm">{message.text || "Ce message n’a pas de texte lisible."}</pre>
        )}
      </div>
    </div>
  );
}

function EmlMeta({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <p className="truncate text-[12px] opacity-80">
      <span className="opacity-60">{label} </span>
      {value}
    </p>
  );
}

function formatMailSize(size: number): string {
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function mailDocument(html: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0;background:#fff;color:#0f172a}
    body{padding:16px 18px 28px;font:15px/1.55 ui-sans-serif,system-ui,sans-serif;word-break:break-word}
    img,video{max-width:100%;height:auto}
    a{color:#4f46e5}
    blockquote{margin:0.6em 0;padding-left:0.8em;border-left:3px solid #e2e8f0;color:#475569}
    pre{white-space:pre-wrap}
  </style></head><body>${html}</body></html>`;
}

function OfficePane({ path, ownerId, mode }: { path: string; ownerId: number; mode: "view" | "edit" }) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    let cancelled = false;
    let editor: { destroyEditor?: () => void } | null = null;
    const box = document.createElement("div");
    box.id = `crm-oo-${Math.random().toString(36).slice(2, 10)}`;
    box.className = "h-full w-full";
    node.replaceChildren(box);
    setError("");
    void officeConfig(path, mode, ownerId)
      .then(async (data) => {
        if (cancelled) return;
        await loadDocs(data.document_server_url);
        if (cancelled || !window.DocsAPI?.DocEditor) throw new Error("Éditeur OnlyOffice indisponible.");
        editor = new window.DocsAPI.DocEditor(box.id, { ...data.config, width: "100%", height: "100%" });
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Document impossible à ouvrir.");
      });
    return () => {
      cancelled = true;
      try {
        editor?.destroyEditor?.();
      } catch {
        /* l’éditeur est déjà parti */
      }
    };
  }, [path, mode, ownerId]);

  return (
    <div className="relative h-full">
      <div ref={host} className="h-full w-full" />
      {error ? <p className="absolute inset-x-0 top-3 text-center text-sm text-rose-700">{error}</p> : null}
    </div>
  );
}

function loadDocs(server: string): Promise<void> {
  if (window.DocsAPI?.DocEditor) return Promise.resolve();
  const base = server.replace(/\/+$/, "");
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-crm-onlyoffice="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("OnlyOffice est injoignable.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = `${base}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.dataset.crmOnlyoffice = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("OnlyOffice est injoignable."));
    document.head.appendChild(script);
  });
}

declare global {
  interface Window {
    DocsAPI?: { DocEditor: new (id: string, config: object) => { destroyEditor?: () => void } };
  }
}
